import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { progressApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineChatAlt2,
  HiPencil,
  HiEye,
  HiTrash,
  HiX,
} from 'react-icons/hi';
import ProgressReportForm from '../../components/Forms/ProgressReportForm';
import SendWhatsAppModal from '../../components/WhatsApp/SendWhatsAppModal';
import TruncatedName from '../../components/TruncatedName';
import { formatCalendarDateDMY, formatDateTimeDMYIST } from '../../utils/timezoneDisplay';
import { downloadPdfFromResponse } from '../../utils/downloadPdf';

function formatReportCreatedLabel(report) {
  if (!report?.created_at) return null;
  const formatted = formatDateTimeDMYIST(report.created_at);
  return formatted === '—' ? null : formatted;
}

function formatReportPeriodLabel(report) {
  if (!report) return null;

  const start = formatCalendarDateDMY(report.report_period_start);
  const end = formatCalendarDateDMY(report.report_period_end);
  const hasStart = report.report_period_start && start !== '—';
  const hasEnd = report.report_period_end && end !== '—';

  if (!hasStart && !hasEnd) return null;
  if (hasStart && hasEnd) return `${start} - ${end}`;
  if (hasStart) return `From ${start}`;
  return `Until ${end}`;
}

const Progress = () => {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const [reports, setReports] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappReport, setWhatsappReport] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchData = async () => {
    try {
      const params = patientId ? { patient_id: patientId } : {};
      const [reportsRes, pendingRes] = await Promise.all([
        progressApi.getAll(params),
        progressApi.getPending()
      ]);
      setReports(reportsRes.data.data.reports || []);
      setPending(pendingRes.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch progress reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (patient = null) => {
    setSelectedReport(null);
    setSelectedPatient(patient);
    setShowForm(true);
  };

  const handleEdit = async (report) => {
    try {
      const response = await progressApi.getById(report.id);
      setSelectedReport(response.data.data);
      setSelectedPatient(null);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load progress report details');
    }
  };

  const handleView = async (report) => {
    try {
      const response = await progressApi.getById(report.id);
      setDetailReport(response.data.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to load progress report details');
    }
  };

  const handleDelete = (report) => {
    setReportToDelete(report);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      await progressApi.delete(reportToDelete.id);
      toast.success('Progress report deleted successfully');
      setShowDeleteConfirm(false);
      setReportToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete progress report');
    }
  };

  const handleDownloadPdf = async (report) => {
    const id = report?.id ?? report;
    const patientCode = report?.patient_code;
    const isHomePlan = report?.report_type === 'home_plan_report';
    const filename = isHomePlan
      ? `Home_Plan_${patientCode || id}.pdf`
      : `Progress_Report_${patientCode || id}.pdf`;

    try {
      const response = await progressApi.getPdf(id);
      await downloadPdfFromResponse(response, filename);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to download PDF');
    }
  };

  const handleWhatsAppReport = (report) => {
    setWhatsappReport(report);
    setShowWhatsAppModal(true);
  };

  const previewProgressWhatsApp = async () => {
    const res = await progressApi.previewWhatsAppSend(whatsappReport.id);
    return res.data.data;
  };

  const sendProgressWhatsApp = async (data) => {
    await progressApi.sendWhatsApp(whatsappReport.id, data);
    fetchData();
    if (detailReport?.id === whatsappReport.id) {
      setDetailReport((prev) => (prev ? { ...prev, status: 'sent' } : prev));
    }
  };

  const getWhatsAppModalTitle = (report) => (
    report?.report_type === 'home_plan_report'
      ? 'Send home plan via WhatsApp'
      : 'Send progress report via WhatsApp'
  );

  const getWhatsAppDocumentLabel = (report) => (
    report?.report_type === 'home_plan_report' ? 'Home plan report' : 'Progress report'
  );

  const handleFormSuccess = () => {
    fetchData();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Progress Reports</h1>
          <p className="text-gray-500 mt-1">30-day progress reports for each child</p>
        </div>
        <button 
          onClick={() => handleCreate()}
          className="btn btn-primary"
        >
          <HiOutlinePlus className="w-5 h-5 mr-2" />
          Create Report
        </button>
      </div>

      {/* Pending reports alert */}
      {pending.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="font-medium text-amber-800">
            {pending.length} patients need 30-day progress reports
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {pending.slice(0, 5).map(p => (
              <button
                key={p.id}
                onClick={() => handleCreate(p)}
                className="badge bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer"
              >
                {p.full_name} ({p.days_since_last} days)
              </button>
            ))}
            {pending.length > 5 && (
              <span className="badge bg-amber-100 text-amber-700">
                +{pending.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Reports list */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No progress reports found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <TruncatedName name={report.patient_name} maxLength={20}>
                      <p className="font-medium text-gray-900" />
                    </TruncatedName>
                    <p className="text-xs text-gray-500 mt-1 capitalize">
                      Type: {String(report.report_type || 'progress_report').replace(/_/g, ' ')}
                    </p>
                    {formatReportCreatedLabel(report) && (
                      <p className="text-sm text-gray-600 mt-1">
                        Report made on {formatReportCreatedLabel(report)}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {formatReportPeriodLabel(report) ? `Period: ${formatReportPeriodLabel(report)}` : 'Period not set'}
                    </p>
                    {report.overall_rating && (
                      <p className="mt-2 text-sm text-gray-600">
                        Rating: <span className="font-medium capitalize">{report.overall_rating.replace('_', ' ')}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${
                      report.status === 'approved' ? 'bg-green-100 text-green-700' :
                      report.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      report.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {report.status.replace('_', ' ')}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleView(report)}
                        className="text-primary-600 hover:text-primary-700 p-1.5"
                        title="View Details"
                      >
                        <HiEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(report)}
                        className="text-green-600 hover:text-green-700 p-1.5"
                        title="Edit Report"
                      >
                        <HiPencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(report)}
                        className="text-blue-600 hover:text-blue-700 p-1.5"
                        title="Download PDF"
                      >
                        <HiOutlineDownload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleWhatsAppReport(report)}
                        className="text-[#128C7E] hover:text-[#075E54] p-1.5"
                        title={
                          report.report_type === 'home_plan_report'
                            ? 'Send home plan via WhatsApp'
                            : 'Send via WhatsApp'
                        }
                      >
                        <HiOutlineChatAlt2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(report)}
                        className="text-red-600 hover:text-red-700 p-1.5"
                        title="Delete Report"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Progress Report Form Modal */}
      {showForm && (
        <ProgressReportForm
          report={selectedReport}
          patient={selectedPatient}
          onClose={() => {
            setShowForm(false);
            setSelectedReport(null);
            setSelectedPatient(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Progress Report Detail Modal */}
      {showDetail && detailReport && (
        <div className="modal-shell flex items-start justify-center overflow-y-auto sm:items-center">
          <div className="modal-panel max-w-4xl w-full min-w-0 my-auto">
            <div className="shrink-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
              <h3 className="text-lg font-semibold">Progress Report Details</h3>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailReport(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="modal-body min-h-0 space-y-4">
              {/* Header Info */}
              <div className="w-full min-w-0 bg-primary-50 p-4 rounded-lg border border-primary-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Patient</p>
                    <p className="font-bold text-gray-900">{detailReport.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`badge ${
                      detailReport.status === 'approved' ? 'bg-green-100 text-green-700' :
                      detailReport.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      detailReport.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {detailReport.status.replace('_', ' ')}
                    </span>
                  </div>
                  {formatReportCreatedLabel(detailReport) && (
                    <div>
                      <p className="text-sm text-gray-600">Report made on</p>
                      <p className="text-gray-900">{formatReportCreatedLabel(detailReport)}</p>
                    </div>
                  )}
                  {formatReportPeriodLabel(detailReport) && (
                    <div>
                      <p className="text-sm text-gray-600">Report Period</p>
                      <p className="text-gray-900">{formatReportPeriodLabel(detailReport)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Report Type</p>
                    <p className="font-semibold text-primary-600 capitalize">
                      {String(detailReport.report_type || 'progress_report').replace(/_/g, ' ')}
                    </p>
                  </div>
                  {detailReport.overall_rating && (
                    <div>
                      <p className="text-sm text-gray-600">Overall Rating</p>
                      <p className="font-semibold text-primary-600 capitalize">
                        {detailReport.overall_rating.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Sections */}
              {detailReport.report_type === 'home_plan_report' && detailReport.home_plan_content && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Home Plan Report Content</label>
                  <div className="w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div
                      className="rich-html-content prose prose-sm max-w-none text-gray-900"
                      dangerouslySetInnerHTML={{ __html: detailReport.home_plan_content }}
                    />
                  </div>
                </div>
              )}

              {detailReport.report_type !== 'home_plan_report' && detailReport.activities_done && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Activities Done</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.activities_done}
                  </p>
                </div>
              )}

              {detailReport.report_type !== 'home_plan_report' && detailReport.improvements && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Improvements Observed</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.improvements}
                  </p>
                </div>
              )}

              {detailReport.report_type !== 'home_plan_report' && detailReport.therapy_response && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Therapy Response</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.therapy_response}
                  </p>
                </div>
              )}

              {detailReport.report_type !== 'home_plan_report' && detailReport.issues_encountered && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Issues Encountered</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.issues_encountered}
                  </p>
                </div>
              )}

              {detailReport.report_type !== 'home_plan_report' && detailReport.next_steps && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Next Steps</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.next_steps}
                  </p>
                </div>
              )}

              {detailReport.doctor_notes && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Doctor Notes</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.doctor_notes}
                  </p>
                </div>
              )}

              {detailReport.parent_feedback && (
                <div className="w-full min-w-0">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Parent Feedback</label>
                  <p className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {detailReport.parent_feedback}
                  </p>
                </div>
              )}

              {/* Metadata */}
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {detailReport.generated_by_name && (
                    <div>
                      <span className="text-gray-600">Generated By:</span>
                      <span className="ml-2 font-medium">{detailReport.generated_by_name}</span>
                    </div>
                  )}
                  {detailReport.reviewed_by_name && (
                    <div>
                      <span className="text-gray-600">Reviewed By:</span>
                      <span className="ml-2 font-medium">{detailReport.reviewed_by_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer flex flex-wrap justify-end gap-3">
              <button
                onClick={() => handleWhatsAppReport(detailReport)}
                className="px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#1da851] flex items-center gap-2"
              >
                <HiOutlineChatAlt2 className="w-4 h-4" />
                {detailReport.report_type === 'home_plan_report'
                  ? 'Send Home Plan via WhatsApp'
                  : 'Send via WhatsApp'}
              </button>
              <button
                onClick={() => handleDownloadPdf(detailReport)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <HiOutlineDownload className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailReport(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <SendWhatsAppModal
        open={showWhatsAppModal && !!whatsappReport}
        onClose={() => {
          setShowWhatsAppModal(false);
          setWhatsappReport(null);
        }}
        title={getWhatsAppModalTitle(whatsappReport)}
        documentLabel={getWhatsAppDocumentLabel(whatsappReport)}
        onPreview={previewProgressWhatsApp}
        onSend={sendProgressWhatsApp}
        showPdfStyle={whatsappReport?.report_type !== 'home_plan_report'}
        textOnly={whatsappReport?.report_type === 'home_plan_report'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && reportToDelete && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Delete Progress Report</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this progress report?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="font-medium">{reportToDelete.patient_name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatReportPeriodLabel(reportToDelete) ? `Period: ${formatReportPeriodLabel(reportToDelete)}` : 'Period not set'}
                </p>
                {formatReportCreatedLabel(reportToDelete) && (
                  <p className="text-sm text-gray-500 mt-1">
                    Report made on {formatReportCreatedLabel(reportToDelete)}
                  </p>
                )}
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ This action cannot be undone.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setReportToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Progress;

